"use client";

import { useEffect, useState, type ElementType } from "react";
import Image from "next/image";
import NextLink from "next/link";
import { useTranslations } from "next-intl";
import { Button, buttonVariants } from "@/components/ui/button";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import BackToTopButton from "@/components/ui/back-to-top-button";
import { Link } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { HUBSPOT_FORMS, HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";
import {
  LOCATION_HIGHLIGHT_ICONS,
  getLocationLandingCopy,
  type LocationLandingConfig,
  type LocationLandingId,
} from "@/data/location-pages";
import { LocationInstallationsGallery } from "@/components/locations/location-installations-gallery";

type GoogleReviewsPayload = {
  rating: number | null;
  userRatingsTotal: number | null;
};

const LOCATION_CITY_LABELS: Record<LocationLandingId, string> = {
  huelva: "Huelva",
  cadiz: "Cádiz",
  andalucia: "Andalucía",
};

const formatCopy = (template: string, values: Record<string, string>) => {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? "");
};

export default function SolarLocationPage({
  locale,
  id,
  config,
}: {
  locale: string;
  id: LocationLandingId;
  config: LocationLandingConfig;
}) {
  const t = useTranslations("locationLandingPages");
  const copy = getLocationLandingCopy(id, (key) => t.raw(key));
  const basePath = locale === routing.defaultLocale ? "" : `/${locale}`;
  const portalId = HUBSPOT_PORTAL_ID;
  const formId = HUBSPOT_FORMS.contact;
  const [googleData, setGoogleData] = useState<GoogleReviewsPayload | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchReviews = async () => {
      try {
        const response = await fetch("/api/google-reviews");
        if (!response.ok) return;
        const data = (await response.json()) as GoogleReviewsPayload;
        if (isMounted) {
          setGoogleData(data);
        }
      } catch {
        // Silent fallback to defaults
      }
    };
    fetchReviews();
    return () => {
      isMounted = false;
    };
  }, []);

  const displayRating = googleData?.rating?.toFixed(1) ?? "4.7";
  const reviewCount = googleData?.userRatingsTotal ?? 7000;
  const reviewCountFormatted = reviewCount.toLocaleString();
  const ratingLabel = formatCopy(copy.reviews.ratingLabel, { rating: displayRating });
  const countLabel = formatCopy(copy.reviews.countLabel, { count: reviewCountFormatted });
  const reviewsSectionBody = formatCopy(copy.reviewsSection.body, { count: reviewCountFormatted });
  const reviewsSectionScore = formatCopy(copy.reviewsSection.scoreLabel, { rating: displayRating });
  const reviewsSectionCount = formatCopy(copy.reviewsSection.countLabel, { count: reviewCountFormatted });
  const IntroHeadingTag = (copy.intro.titleTag ?? "h2") as ElementType;
  const AboutHeadingTag = (copy.about.titleTag ?? "h3") as ElementType;
  const CoverageHeadingTag = (copy.coverage.titleTag ?? "h3") as ElementType;
  const CtaHeadingTag = (copy.ctaBlock.titleTag ?? "h3") as ElementType;

  return (
    <div className="min-h-screen bg-surface-light text-ink dark:bg-surface-dark dark:text-white">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Image src={config.heroImage} alt={copy.hero.alt} fill className="object-cover" priority />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy-deep/80 via-brand-navy-deep/70 to-brand-navy-deep/90" />
        </div>
        <div className="relative mx-auto flex max-w-6xl flex-col items-center px-4 py-16 text-center text-white sm:py-20">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-display text-gold-soft">
            {copy.hero.eyebrow}
          </p>
          <h1 className="mt-3 type-h1">{copy.hero.title}</h1>
          <p className="mt-4 max-w-2xl text-sm sm:text-lg text-mist">{copy.hero.subtitle}</p>
          <div className="mt-6 rounded-2xl bg-white/95 px-6 py-4 text-ink shadow-xl">
            <div className="flex items-center justify-center gap-2">
              <div className="relative h-6 w-28">
                <svg viewBox="0 0 140 24" className="h-full w-full" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <path
                      key={index}
                      d="M12 2l2.4 7.4h7.6l-6 4.6 2.4 7.4-6-4.6-6 4.6 2.4-7.4-6-4.6h7.6z"
                      transform={`translate(${index * 28}, 0)`}
                      fill="#FFC107"
                    />
                  ))}
                </svg>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-brand-navy">{ratingLabel}</span>
            </div>
            <p className="mt-2 text-[11px] sm:text-xs text-gray-600">{countLabel}</p>
          </div>
        </div>
      </section>

      <section className="px-4 section-y">
        <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
          <IntroHeadingTag className="text-2xl sm:text-3xl font-black text-brand-navy dark:text-white">
            {copy.intro.title}
          </IntroHeadingTag>
          <p className="mt-3 max-w-2xl text-sm sm:text-base text-gray-600 dark:text-gray-300">{copy.intro.subtitle}</p>
          {copy.intro.extraParagraphs?.map((paragraph, index) => (
            <p key={`${copy.intro.title}-${index}`} className="mt-3 max-w-2xl text-sm sm:text-base text-gray-600 dark:text-gray-300">
              {paragraph}
            </p>
          ))}
          <Button asChild className={buttonVariants({ variant: "pillNavy", size: "pill", className: "mt-5" })}>
            <Link href={`${basePath}/presupuesto-placas-solares`}>{copy.intro.cta}</Link>
          </Button>
        </div>
      </section>

      <section className="px-4 pb-10">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative min-h-[260px] overflow-hidden rounded-card shadow-lg">
            <Image src={config.aboutImage} alt={copy.about.imageAlt} fill className="object-cover" />
          </div>
          <div className="surface-card p-6">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{copy.about.eyebrow}</p>
            <AboutHeadingTag className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">
              {copy.about.title}
            </AboutHeadingTag>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{copy.about.copy}</p>
            <div className="mt-5 flex flex-col gap-3 text-sm text-gray-700 dark:text-gray-200">
              <p>
                <strong>{copy.about.phoneLabel} </strong>
                <a href={config.phoneHref} className="text-brand-red">
                  {copy.about.phoneNumber}
                </a>
              </p>
              <p>
                <strong>{copy.about.addressLabel} </strong>
                {copy.about.address}
              </p>
              <Button asChild className="mt-6 w-full rounded-full px-6 py-3 text-sm font-semibold">
                <a href={config.phoneHref}>{copy.formBlock.sidebarCall}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LocationInstallationsGallery city={LOCATION_CITY_LABELS[id]} />

      <section className="px-4 pb-12">
        <div className="mx-auto max-w-6xl rounded-card border border-brand-navy/10 bg-white p-6 shadow-card dark:border-white/10 dark:bg-surface-dark-raised">
          <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{copy.coverage.eyebrow}</p>
          <CoverageHeadingTag className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">
            {copy.coverage.title}
          </CoverageHeadingTag>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{copy.coverage.subtitle}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {copy.coverage.items.map((item) => (
              <div
                key={item}
                className="rounded-xl border border-brand-navy/15 bg-surface-light px-4 py-3 text-sm font-semibold text-brand-navy dark:border-white/10 dark:bg-surface-dark-raised dark:text-white"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-12">
        <div className="mx-auto grid max-w-6xl gap-6">
          <div className="surface-card p-6">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{copy.formBlock.eyebrow}</p>
            <h3 className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{copy.formBlock.title}</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{copy.formBlock.subtitle}</p>
            <div className="hubspot-light mt-6 rounded-card bg-surface-light p-4 dark:bg-surface-dark-raised">
              <HubSpotForm portalId={portalId} formId={formId} region={HUBSPOT_REGION} />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-brand-navy to-brand-navy-light px-4 section-y text-white">
        <div className="mx-auto max-w-6xl text-center">
          <h3 className="text-2xl sm:text-3xl font-black">{copy.benefits.title}</h3>
          <p className="mt-3 text-sm sm:text-base text-white/80">{copy.benefits.subtitle}</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {copy.highlights.map((item, idx) => {
              const icon = LOCATION_HIGHLIGHT_ICONS[idx % LOCATION_HIGHLIGHT_ICONS.length];
              return (
                <div key={item.title} className="rounded-2xl bg-white/12 p-5 text-left shadow-xl backdrop-blur">
                  <div className="flex items-start gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 p-3">
                      <Image src={icon} alt={item.title} width={36} height={36} className="brightness-0 invert" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-semibold">{item.title}</h4>
                      <p className="mt-2 text-sm text-white/85">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 section-y">
        <div className="mx-auto max-w-6xl">
          <h3 className="text-center text-2xl sm:text-3xl font-black text-brand-navy dark:text-white">
            {copy.steps.title}
          </h3>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">{copy.steps.subtitle}</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {copy.steps.items.map((step, idx) => (
              <div
                key={`${step.title}-${idx}`}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-surface-dark-raised"
              >
                <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{step.title}</p>
                <h4 className="mt-2 text-lg font-bold text-brand-navy dark:text-white">{step.subtitle}</h4>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-14">
        <div className="mx-auto max-w-6xl grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="surface-card p-6">
            <CtaHeadingTag className="text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">
              {copy.ctaBlock.title}
            </CtaHeadingTag>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{copy.ctaBlock.copy}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild className={buttonVariants({ variant: "pill", size: "pill" })}>
                <Link href={`${basePath}/estudio-gratis`}>{copy.ctaBlock.primary}</Link>
              </Button>
              <Button
                variant="outline"
                className="rounded-full border-brand-navy px-6 py-3 text-sm font-semibold text-brand-navy hover:bg-brand-navy hover:text-white dark:border-white dark:text-white"
              >
                <Link href={`${basePath}/contacto`}>{copy.ctaBlock.secondary}</Link>
              </Button>
            </div>
          </div>
          <div className="relative min-h-[260px] overflow-hidden rounded-card shadow-lg">
            <Image src={config.ctaImage} alt={copy.ctaBlock.imageAlt} fill className="object-cover" />
          </div>
        </div>
      </section>

      <section className="px-4 section-y bg-white dark:bg-surface-dark">
        <div className="mx-auto max-w-6xl">
          <h3 className="text-center text-2xl sm:text-3xl font-black text-brand-navy dark:text-white">
            {copy.reviewsSection.title}
          </h3>
          <div className="mt-8 grid gap-8 lg:grid-cols-2 items-center rounded-card bg-gradient-to-br from-white to-gray-50 px-8 py-8 shadow-2xl dark:from-surface-dark-raised dark:to-[#1a2332]">
            <div className="flex flex-col items-center gap-4">
              <div className="relative h-10 w-48">
                <svg viewBox="0 0 240 40" className="h-full w-full" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <path
                      key={index}
                      d="M20 3.33l4 12.34h13l-10.5 7.66 4 12.34L20 27.67l-10.5 7.66 4-12.34L3 15.67h13z"
                      transform={`translate(${index * 48}, 0)`}
                      fill="#FFC107"
                    />
                  ))}
                </svg>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-brand-navy dark:text-white">{reviewsSectionScore}</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">{copy.reviewsSection.ratingCaption}</p>
                <p className="mt-2 text-lg font-semibold text-gray-700 dark:text-gray-200">{reviewsSectionCount}</p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <h4 className="text-lg font-bold text-brand-navy dark:text-white">{copy.reviewsSection.headline}</h4>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{reviewsSectionBody}</p>
              </div>
              <div className="space-y-3">
                {copy.reviewsSection.bullets.map((bullet) => (
                  <div key={bullet} className="flex items-start gap-3">
                    <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{bullet}</span>
                  </div>
                ))}
              </div>
              <Button asChild className={buttonVariants({ variant: "pillNavy", size: "pill", className: "mt-2" })}>
                <NextLink href={config.googleReviewsUrl} target="_blank" rel="noopener noreferrer">
                  {copy.reviewsSection.cta}
                </NextLink>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <BackToTopButton />
    </div>
  );
}
